import { useMemo } from 'react'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import { API_URL } from '@/utils/constants'

/// Live USD prices for alkanes tokens, keyed by the collection slug the
/// wallet's balance rows carry (`alkane-<block>:<tx>`).
///
/// Alkanes are AMM-traded, not order-booked. The marketplace floor feed
/// (`/tokens/prices`) only knows about the handful with OW listings, and a
/// single stale ask sets that "floor" — DIESEL was quoted there at $424.66
/// against a real pool price of $40.09, overstating a holder's balance by
/// 10.6x, while CheekyB had no entry at all and read as worthless. The pool
/// price is the only honest source for these, and it is the same one
/// /alkanes quotes and trades against.
export default function useAlkanesPrices(): {
	/// USD per whole token, or null when no pool prices this asset. Null is a
	/// real answer — never fall back to a marketplace floor for an alkane.
	priceForSlug: (slug?: string | null) => number | null
	isLoading: boolean
} {
	const { data, isLoading } = useSwr(`${API_URL}/alkane/assets`, fetcher)

	const bySlug = useMemo(() => {
		const map = new Map<string, number>()
		// The endpoint answers `{ assets: [...] }`; older pods answered a bare
		// array. Read both — reading only one is how this shipped priced
		// nothing at all the first time.
		const rows = Array.isArray(data?.assets) ? data.assets : Array.isArray(data) ? data : []
		for (const row of rows) {
			const id = row?.id
			const price = Number(row?.price_usd)
			if (typeof id === 'string' && Number.isFinite(price) && price > 0) {
				map.set(`alkane-${id}`, price)
			}
		}
		return map
	}, [data])

	return useMemo(
		() => ({
			priceForSlug: (slug?: string | null) => (slug ? bySlug.get(slug) ?? null : null),
			isLoading
		}),
		[bySlug, isLoading]
	)
}
